import { Checkbox } from '@affine/component';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { DisclosureChevron } from '@affine/core/components/animation/disclosure-chevron';
import { useI18n } from '@affine/i18n';
import {
  CloseIcon,
  DownloadIcon,
  InformationIcon,
  ReauthenticationIcon,
} from '@blocksuite/icons/rc';
import { Fragment, type ReactNode, useState } from 'react';

import {
  getSigningProfileStateHint,
  getSigningProfileStateLabel,
} from '../../../desktop/dialogs/setting/workspace-setting/signatures/signing-profile-labels';
import {
  type DigitalSigningStep,
  resolveSigningProfileRemedy,
} from '../../../desktop/dialogs/setting/workspace-setting/signatures/signing-readiness';
import type {
  PdfSigningJobInfo,
  SignatureSigningProfileInfo,
  SignedPdfVersionInfo,
} from '../../../desktop/dialogs/setting/workspace-setting/signatures/use-workspace-pdf-signatures';
import {
  getPlaceSignedPdfCopyLabel,
  getPlacingSignedPdfCopyLabel,
} from './pdf-signed-version-labels';
import {
  describeSignedPdfVersion,
  formatSignedPdfVersionName,
  formatSignedPdfVersionSigningTime,
  getSignatureValidationStateLabel,
  getSignatureValidationTone,
  getSignedPdfVersionSigner,
  getSignedPdfVersionTrustNote,
  getSigningJobStatusLabel,
} from './pdf-signing-status-labels';
import * as styles from './styles.css';

type SigningStep = DigitalSigningStep<SignatureSigningProfileInfo>;

/** How many signed copies the panel lists; older ones stay in the document. */
const LISTED_SIGNED_COPIES = 3;

export interface PdfDigitalSignaturePanelProps {
  step: SigningStep;
  loadErrorMessage: string | null;
  isBusy: boolean;

  selectedProfileId: string;
  onSelectProfile: (profileId: string) => void;
  hasMarkups: boolean;
  includeMarkups: boolean;
  onIncludeMarkupsChange: (include: boolean) => void;

  job: PdfSigningJobInfo | null;
  isPreparing: boolean;
  canStart: boolean;
  onStart: () => void;
  onCancelJob: (() => void) | null;

  signedVersions: ReadonlyArray<SignedPdfVersionInfo>;
  newestVersionId: string | null;
  canPlace: boolean;
  placingVersionId: string | null;
  onPlace: (version: SignedPdfVersionInfo) => void;
  downloadUrl: (versionId: string) => string;

  onActivateProfile: (profile: SignatureSigningProfileInfo) => void;
  onOpenPersonalSettings: () => void;
  /** Only admins can settle what is missing at the workspace. */
  onOpenWorkspaceSettings: (() => void) | null;
  /** Only owners and admins of a workspace that can buy plans get there. */
  onOpenPlans: (() => void) | null;
}

function isJobRunning(job: PdfSigningJobInfo | null) {
  return (
    job?.status === 'queued' ||
    job?.status === 'running' ||
    job?.status === 'finalizing'
  );
}

function describeProfile(profile: SignatureSigningProfileInfo) {
  return [profile.connectionName, profile.certificateName]
    .filter(Boolean)
    .join(' · ');
}

/** The one thing that is still missing, with the way to settle it. */
function NextStep({
  testId,
  title,
  chip,
  body,
  reason,
  action,
}: {
  testId: string;
  title: string;
  chip?: ReactNode;
  body: string;
  reason?: string | null;
  action?: ReactNode;
}) {
  return (
    <div className={styles.pdfSigningNextStep} data-testid={testId}>
      <div className={styles.pdfSigningNextStepHead}>
        <InformationIcon aria-hidden />
        <span className={styles.pdfSigningNextStepText}>
          <span className={styles.pdfSigningNextStepTitle}>{title}</span>
          <span className={styles.pdfSigningNextStepBody}>{body}</span>
          {reason ? (
            <span className={styles.pdfSigningReason}>{reason}</span>
          ) : null}
        </span>
        {chip}
      </div>
      {action}
    </div>
  );
}

function StepButton({
  testId,
  label,
  disabled,
  onClick,
}: {
  testId: string;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.pdfSignaturePanelButton}
      data-full-width="true"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
    >
      <span>{label}</span>
    </button>
  );
}

/**
 * The digital signature panel of the PDF reader, in two zones. "Sign" shows
 * exactly one state: the next missing step with the way to it, or the signing
 * itself. "Signed copies" lists what signing has produced.
 */
export function PdfDigitalSignaturePanel({
  step,
  loadErrorMessage,
  isBusy,
  selectedProfileId,
  onSelectProfile,
  hasMarkups,
  includeMarkups,
  onIncludeMarkupsChange,
  job,
  isPreparing,
  canStart,
  onStart,
  onCancelJob,
  signedVersions,
  newestVersionId,
  canPlace,
  placingVersionId,
  onPlace,
  downloadUrl,
  onActivateProfile,
  onOpenPersonalSettings,
  onOpenWorkspaceSettings,
  onOpenPlans,
}: PdfDigitalSignaturePanelProps) {
  const t = useI18n();
  const [detailsVersionId, setDetailsVersionId] = useState<string | null>(null);
  const running = isJobRunning(job);

  const renderStep = () => {
    switch (step.kind) {
      case 'hidden':
        return null;
      case 'upgrade_plan':
        return (
          <NextStep
            testId="pdf-signing-step-upgrade-plan"
            title={t[
              'com.streamlain.pdf.signature.digital.step.upgradePlan.title'
            ]()}
            body={
              step.requiredPlan
                ? t[
                    'com.streamlain.pdf.signature.digital.step.upgradePlan.body'
                  ]({ plan: step.requiredPlan })
                : t[
                    'com.streamlain.pdf.signature.digital.step.upgradePlan.bodyNoPlan'
                  ]()
            }
            action={
              onOpenPlans ? (
                <StepButton
                  testId="pdf-signing-step-action"
                  label={t['com.streamlain.ai-gate.view-plans']()}
                  onClick={onOpenPlans}
                />
              ) : null
            }
          />
        );
      case 'connect_service':
        return (
          <NextStep
            testId="pdf-signing-step-connect-service"
            title={t[
              'com.streamlain.pdf.signature.digital.step.connectService.title'
            ]()}
            body={t[
              'com.streamlain.pdf.signature.digital.step.connectService.body'
            ]()}
            action={
              onOpenWorkspaceSettings ? (
                <StepButton
                  testId="pdf-signing-step-action"
                  label={t[
                    'com.streamlain.pdf.signature.digital.step.connectService.action'
                  ]()}
                  onClick={onOpenWorkspaceSettings}
                />
              ) : null
            }
          />
        );
      case 'create_profile':
        return (
          <NextStep
            testId="pdf-signing-step-create-profile"
            title={t[
              'com.streamlain.pdf.signature.digital.step.createProfile.title'
            ]()}
            body={t[
              'com.streamlain.pdf.signature.digital.step.createProfile.body'
            ]()}
            action={
              <StepButton
                testId="pdf-signing-step-action"
                label={t[
                  'com.streamlain.pdf.signature.digital.step.createProfile.action'
                ]()}
                onClick={onOpenPersonalSettings}
              />
            }
          />
        );
      case 'fix_profile': {
        const remedy = resolveSigningProfileRemedy(step.state);
        const action =
          remedy === 'activate' ? (
            <StepButton
              testId="pdf-signing-step-action"
              label={t['com.streamlain.pdf.signature.profile.activate']()}
              disabled={isBusy}
              onClick={() => onActivateProfile(step.profile)}
            />
          ) : remedy === 'personal_settings' ? (
            <StepButton
              testId="pdf-signing-step-action"
              label={t[
                'com.streamlain.pdf.signature.digital.step.openPersonal'
              ]()}
              onClick={onOpenPersonalSettings}
            />
          ) : onOpenWorkspaceSettings ? (
            <StepButton
              testId="pdf-signing-step-action"
              label={t[
                'com.streamlain.pdf.signature.digital.step.openWorkspace'
              ]()}
              onClick={onOpenWorkspaceSettings}
            />
          ) : null;
        return (
          <NextStep
            testId="pdf-signing-step-fix-profile"
            title={step.profile.name}
            chip={
              <span
                className={styles.pdfSigningChip}
                data-tone={
                  step.state === 'needs_activation' ? 'warning' : 'danger'
                }
              >
                {getSigningProfileStateLabel(step.state)}
              </span>
            }
            body={getSigningProfileStateHint(step.state)}
            reason={
              step.state === 'activation_failed'
                ? step.profile.failureReason
                : null
            }
            action={action}
          />
        );
      }
      case 'ready': {
        const selectable = step.profiles.length > 1;
        return (
          <>
            <div
              className={styles.pdfSigningProfileList}
              role={selectable ? 'radiogroup' : undefined}
              aria-label={t['com.streamlain.pdf.signature.digital.signAs']()}
              data-testid="pdf-signing-profiles"
            >
              {step.profiles.map(profile => {
                const text = (
                  <>
                    <ReauthenticationIcon aria-hidden />
                    <span className={styles.pdfSigningProfileText}>
                      <span className={styles.pdfSigningProfileName}>
                        {profile.name}
                      </span>
                      <span className={styles.pdfSigningProfileMeta}>
                        {describeProfile(profile)}
                      </span>
                    </span>
                    {profile.isDefault && selectable ? (
                      <span className={styles.pdfSigningChip}>
                        {t['com.streamlain.pdf.signature.settings.default']()}
                      </span>
                    ) : null}
                  </>
                );
                return selectable ? (
                  <button
                    key={profile.id}
                    type="button"
                    role="radio"
                    aria-checked={profile.id === selectedProfileId}
                    className={styles.pdfSigningProfile}
                    data-selectable="true"
                    data-testid={`pdf-signing-profile-${profile.id}`}
                    disabled={running || isPreparing}
                    onClick={() => onSelectProfile(profile.id)}
                  >
                    {text}
                  </button>
                ) : (
                  <div
                    key={profile.id}
                    className={styles.pdfSigningProfile}
                    data-testid={`pdf-signing-profile-${profile.id}`}
                  >
                    {text}
                  </div>
                );
              })}
            </div>
            {hasMarkups ? (
              <label className={styles.pdfSignatureCheckboxOption}>
                <Checkbox
                  checked={includeMarkups}
                  name="pdf-signing-include-annotations"
                  onChange={(_, checked) => onIncludeMarkupsChange(checked)}
                />
                <span className={styles.pdfPrintOptionText}>
                  <span className={styles.pdfPrintOptionTitle}>
                    {t['com.streamlain.pdf.signature.digital.includeMarkups']()}
                  </span>
                  <span className={styles.pdfPrintOptionDescription}>
                    {t[
                      'com.streamlain.pdf.signature.digital.includeMarkups.description'
                    ]()}
                  </span>
                </span>
              </label>
            ) : null}
            {job?.status === 'failed' || job?.status === 'cancelled' ? (
              <div
                className={styles.pdfSignatureInlineStatus}
                data-testid="pdf-signing-job-outcome"
              >
                <InformationIcon aria-hidden />
                <span>
                  {[getSigningJobStatusLabel(job.status), job.failureReason]
                    .filter(Boolean)
                    .join(': ')}
                </span>
              </div>
            ) : null}
            <div className={styles.pdfSignaturePanelActions}>
              <button
                type="button"
                className={styles.pdfSignaturePanelButton}
                data-full-width="true"
                data-testid="pdf-signing-create"
                disabled={!canStart}
                onClick={onStart}
              >
                <ReauthenticationIcon aria-hidden />
                <span>
                  {isPreparing
                    ? t['com.streamlain.pdf.signature.digital.preparing']()
                    : running && job
                      ? getSigningJobStatusLabel(job.status)
                      : t['com.streamlain.pdf.signature.digital.create']()}
                </span>
              </button>
              {onCancelJob ? (
                <button
                  type="button"
                  className={styles.pdfSignaturePanelButton}
                  data-variant="secondary"
                  data-full-width="true"
                  data-testid="pdf-signing-cancel"
                  onClick={onCancelJob}
                >
                  <CloseIcon aria-hidden />
                  <span>
                    {t['com.streamlain.pdf.signature.digital.cancel']()}
                  </span>
                </button>
              ) : null}
            </div>
            <p className={styles.pdfSignaturePanelMeta}>
              {t['com.streamlain.pdf.signature.digital.description']()}
            </p>
          </>
        );
      }
    }
  };

  const listed = signedVersions.slice(0, LISTED_SIGNED_COPIES);

  return (
    <>
      <section
        className={styles.pdfSignaturePanelSection}
        data-testid="pdf-signing-zone"
        data-step={step.kind}
      >
        <div className={styles.pdfSignaturePanelSectionHeader}>
          <span className={styles.pdfSignaturePanelSectionTitle}>
            {step.kind === 'ready'
              ? t['com.streamlain.pdf.signature.digital.signAs']()
              : t['com.streamlain.pdf.signature.digital.nextStep']()}
          </span>
          {step.kind === 'ready' ? (
            <button
              type="button"
              className={styles.pdfSignaturePanelLink}
              data-testid="pdf-signing-manage-profiles"
              onClick={onOpenPersonalSettings}
            >
              {t['com.streamlain.pdf.signature.digital.manageProfiles']()}
            </button>
          ) : null}
        </div>
        {loadErrorMessage ? (
          <div className={styles.pdfSignatureInlineStatus}>
            <InformationIcon aria-hidden />
            <span>{loadErrorMessage}</span>
          </div>
        ) : null}
        {renderStep()}
      </section>

      {listed.length > 0 ? (
        <section
          className={styles.pdfSignaturePanelSection}
          data-testid="pdf-signed-copies"
        >
          <div className={styles.pdfSignaturePanelSectionHeader}>
            <span className={styles.pdfSignaturePanelSectionTitle}>
              {t['com.streamlain.pdf.signature.versions.title']()}
            </span>
          </div>
          <div className={styles.pdfSignedCopyList}>
            {listed.map(version => {
              const isNew = version.id === newestVersionId;
              const isPlacing = placingVersionId === version.id;
              const detailsOpen = detailsVersionId === version.id;
              const trustNote = getSignedPdfVersionTrustNote(version);
              return (
                <div
                  key={version.id}
                  className={styles.pdfSignedCopy}
                  data-new={isNew ? 'true' : undefined}
                  data-testid={`pdf-signed-copy-${version.id}`}
                >
                  <div className={styles.pdfSignedCopyHead}>
                    <span className={styles.pdfSignedCopyText}>
                      <span className={styles.pdfSignedCopyName}>
                        {formatSignedPdfVersionName(version)}
                      </span>
                      <span className={styles.pdfSignedCopyMeta}>
                        {`${getSignedPdfVersionSigner(version)} · ${formatSignedPdfVersionSigningTime(version)}`}
                      </span>
                    </span>
                    {isNew ? (
                      <span className={styles.pdfSigningChip} data-tone="info">
                        {t['com.streamlain.pdf.signature.versions.new']()}
                      </span>
                    ) : null}
                    <span
                      className={styles.pdfSigningChip}
                      data-tone={getSignatureValidationTone(
                        version.validationState
                      )}
                      data-testid={`pdf-signed-copy-state-${version.id}`}
                    >
                      {getSignatureValidationStateLabel(
                        version.validationState
                      )}
                    </span>
                  </div>
                  <div className={styles.pdfSignedCopyActions}>
                    {canPlace ? (
                      <button
                        type="button"
                        className={styles.pdfSignedCopyAction}
                        data-variant={isNew ? 'primary' : undefined}
                        data-testid={`pdf-signed-copy-place-${version.id}`}
                        disabled={placingVersionId !== null}
                        onClick={() => onPlace(version)}
                      >
                        <span>
                          {isPlacing
                            ? getPlacingSignedPdfCopyLabel()
                            : getPlaceSignedPdfCopyLabel()}
                        </span>
                      </button>
                    ) : null}
                    <a
                      className={styles.pdfSignedCopyAction}
                      data-testid={`pdf-signed-copy-download-${version.id}`}
                      href={downloadUrl(version.id)}
                      download={version.fileName || undefined}
                    >
                      <DownloadIcon aria-hidden />
                      <span>
                        {t['com.streamlain.pdf.signature.versions.download']()}
                      </span>
                    </a>
                    <button
                      type="button"
                      className={styles.pdfSignedCopyAction}
                      aria-expanded={detailsOpen}
                      data-testid={`pdf-signed-copy-details-${version.id}`}
                      onClick={() =>
                        setDetailsVersionId(detailsOpen ? null : version.id)
                      }
                    >
                      <span>
                        {t['com.streamlain.pdf.signature.versions.details']()}
                      </span>
                      <DisclosureChevron expanded={detailsOpen} />
                    </button>
                  </div>
                  <AnimatedRegion expanded={detailsOpen}>
                    <div
                      className={styles.pdfSignedCopyDetailsBody}
                      data-testid={`pdf-signed-copy-details-body-${version.id}`}
                    >
                      <dl className={styles.pdfSignedCopyDetails}>
                        {describeSignedPdfVersion(version).map(detail => (
                          <Fragment key={detail.label}>
                            <dt>{detail.label}</dt>
                            <dd>{detail.value}</dd>
                          </Fragment>
                        ))}
                      </dl>
                      {trustNote ? (
                        <p className={styles.pdfSignedCopyNote}>{trustNote}</p>
                      ) : null}
                    </div>
                  </AnimatedRegion>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
    </>
  );
}
